import React, { forwardRef } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  FormControlProps,
  Input,
  InputGroup,
  InputRightElement,
  InputProps,
  Spinner,
} from '@chakra-ui/react';
import { WarningTwoIcon, CheckIcon } from '@chakra-ui/icons';

export interface InputFieldProps extends FormControlProps {
  id: string;
  label?: string;
  helperText?: string;
  errorText?: string;
  isValid?: Boolean;
  placeholder?: string;
  inputProps?: InputProps;
  isLoading?: Boolean;
}

/**
 * A component to use in place of chakra input fields, for convenience.
 *
 * Note that top-level placeholder prop is passed to child input for convenience.
 */
export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  ({ id, label, helperText, errorText, isValid, inputProps, isLoading, ...props }, ref) => {
    return (
      <FormControl id={id} {...props}>
        {label && (
          <FormLabel fontFamily="Poppins" mb={1} color="text.base">
            {label}
          </FormLabel>
        )}
        <InputGroup>
          <Input
            {...inputProps}
            _hover={{ borderColor: 'secondary.base' }}
            placeholder={props.placeholder || inputProps?.placeholder}
            ref={ref}
          />
          {props.isInvalid && (
            <InputRightElement height="100%" zIndex="1">
              <WarningTwoIcon fontSize="sm" color="error" />
            </InputRightElement>
          )}
          {isValid && (
            <InputRightElement height="100%" zIndex="1">
              <CheckIcon fontSize="sm" color="secondary.base" />
            </InputRightElement>
          )}
          {isLoading && (
            <InputRightElement height="100%" zIndex="1">
              <Spinner color="primary.base" />
            </InputRightElement>
          )}
        </InputGroup>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
        <FormErrorMessage>{errorText}</FormErrorMessage>
      </FormControl>
    );
  },
);
